/**
 * External Imports
 */
import { Dispatch, FC, SetStateAction, useEffect, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

/**
 * Internal Imports
 */
import AIDETValidationListTable from '../../../components/customTables/validation/aidetListTable';
import SearchHeader from '../../../components/searchHeader';
import ValidationSideNav from '../../../components/sideNav/ValidationSideNav';
import { RootState } from '../../../redux/store';
import { getValidationSubjectsToRoundForList } from '../../../redux/actions';

/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  Employee,
  RoundHistory
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import { ValidationContainer } from '../styles/Validation.styles';

export interface AIDETValidationListProps {
  authorizedUser?: AuthUserProps;
  validationSubjectsToRound: (Employee & RoundHistory)[];
  selectedDeptsFromStore: string[];
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}

const AIDETValidationList: FC<AIDETValidationListProps> = ({
  authorizedUser,
  validationSubjectsToRound = [],
  selectedDeptsFromStore,
  searchTerm,
  setSearchTerm
}) => {
  const dispatch = useDispatch();

  const { validationFilter = 'all' } = useParams();

  const filteredValidationRounds = useMemo(() => {
    return validationSubjectsToRound.filter(employee => {
      const employeeDept =
        (employee.adHoc ? employee?.employeeDept : employee?.dept) || 'Unknown';
      return selectedDeptsFromStore.some(
        selectedDept => selectedDept === employeeDept
      );
    });
  }, [validationSubjectsToRound, selectedDeptsFromStore]);

  const validationCounts = useMemo(() => {
    const validationSubjects = filteredValidationRounds.filter(
      employee => !employee.adHoc
    );
    const validationAdHocs = filteredValidationRounds.filter(
      employee => employee.adHoc
    );

    const completedCount =
      validationSubjects.filter(
        employee => employee?.complianceStatus === 'Completed'
      ).length + validationAdHocs.length;

    const toDoCount = validationSubjects.filter(
      employee =>
        employee?.complianceStatus === 'Todo' ||
        employee?.complianceStatus === 'Overdue' ||
        !employee.complianceStatus
    ).length;

    const expertCount = validationSubjects.filter(employee =>
      employee.lastRound?.tag.includes('Validation_AIDET_E')
    ).length;

    const competentCount = validationSubjects.filter(employee =>
      employee.lastRound?.tag.includes('Validation_AIDET_C')
    ).length;

    const developingCount = validationSubjects.filter(employee =>
      employee.lastRound?.tag.includes('Validation_AIDET_D')
    ).length;

    return {
      total: filteredValidationRounds.length,
      completedCount,
      toDoCount,
      expertCount,
      competentCount,
      developingCount
    };
  }, [filteredValidationRounds]);

  useEffect(() => {
    if (authorizedUser?.hcaid)
      dispatch(
        getValidationSubjectsToRoundForList({
          hcaid: authorizedUser.hcaid,
          type: 'aidetValidation'
        })
      );
  }, []);

  return (
    <ValidationContainer id="AIDET-Validation-Container">
      <ValidationSideNav
        type="aidet"
        total={validationCounts.total}
        completedCount={validationCounts.completedCount}
        toDoCount={validationCounts.toDoCount}
        expertCount={validationCounts.expertCount}
        competentCount={validationCounts.competentCount}
        developingCount={validationCounts.developingCount}
      />
      <div className="d-flex flex-column w-100">
        <SearchHeader searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        <AIDETValidationListTable
          validationFilter={validationFilter}
          searchTerm={searchTerm}
          validationRounds={filteredValidationRounds}
        />
      </div>
    </ValidationContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser,
    validationSubjectsToRound:
      state.EmployeeReducer.validationSubjectsToRoundForList,
    selectedDeptsFromStore: state.EmployeeReducer.selectedDepts
  };
};

export default connect(mapReduxStateToProps)(AIDETValidationList);
